import React from 'react';
import { Skeleton, TableCell, TableRow } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useDelayedFlag } from '../../components/ui';

export interface SkeletonColumn {
  key: string;
  align?: 'left' | 'right';
  /** The same `sx` the real column uses, so hidden columns stay hidden while loading. */
  sx?: SxProps<Theme>;
  /** Placeholder bar width. */
  width?: number | string;
}

/**
 * Table skeleton rows that follow the table's responsive columns, so the loading
 * state has exactly the columns the header shows and nothing jumps when data arrives.
 * Revealed after 300ms so fast loads don't flash.
 */
const ResponsiveSkeletonRows: React.FC<{ columns: SkeletonColumn[]; rows?: number }> = ({ columns, rows = 6 }) => {
  const visible = useDelayedFlag(true, 300);
  return (
    <>
      {Array.from({ length: rows }).map((_, row) => (
        <TableRow key={row} aria-hidden="true" sx={{ '&&:hover': { bgcolor: 'transparent' } }}>
          {columns.map((column, col) => (
            <TableCell key={column.key} align={column.align} sx={column.sx}>
              <Skeleton
                variant="rounded"
                height={14}
                width={column.width ?? (col === 0 ? '70%' : `${40 + ((row * 7 + col * 13) % 40)}%`)}
                sx={{ visibility: visible ? 'visible' : 'hidden', ml: column.align === 'right' ? 'auto' : 0 }}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
};

export default ResponsiveSkeletonRows;
